import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import { Icon } from "../components/Icon";
import { applyTranscriptionFeatureAvailability, DEFAULT_TRANSCRIPTION_SETTINGS, TranscriptionSettings, type TranscriptionFeatureAvailability } from "../components/TranscriptionSettings";
import type { LibraryFolder, LibraryTag } from "../lib/api";
import type { TranscriptionSettings as Settings } from "../lib/api";
import { localizedError } from "../lib/localized-error";
import { useTasks } from "../tasks";
import { TaskTable } from "./DashboardPage";

export function LibraryPage() {
  const { t, i18n } = useTranslation();
  const { api } = useAuth();
  const { tasks: sharedTasks, refresh: refreshShared } = useTasks();
  const [tasks, setTasks] = useState(sharedTasks);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [folderId, setFolderId] = useState("all");
  const [tagId, setTagId] = useState("all");
  const [language, setLanguage] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [folders, setFolders] = useState<LibraryFolder[]>([]);
  const [tags, setTags] = useState<LibraryTag[]>([]);
  const [bulkAction, setBulkAction] = useState<"move" | "tag" | null>(null);
  const [actionValue, setActionValue] = useState("");
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const [createKind, setCreateKind] = useState<"folder" | "tag" | null>(null);
  const [createName, setCreateName] = useState("");
  const [deletePending, setDeletePending] = useState(false);
  const [reprocessOpen, setReprocessOpen] = useState(false);
  const [reprocessSettings, setReprocessSettings] = useState<Settings>(() => structuredClone(DEFAULT_TRANSCRIPTION_SETTINGS));
  const [availability, setAvailability] = useState<TranscriptionFeatureAvailability | undefined>();

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const createdAfter = dateRange === "7d"
        ? Date.now() - 7 * 86_400_000
        : dateRange === "30d" ? Date.now() - 30 * 86_400_000 : undefined;
      setTasks(await api.listTranscriptions({
        q: query.trim() || undefined,
        status: status === "all" ? undefined : status,
        folder_id: folderId === "all" ? undefined : folderId,
        tag_id: tagId === "all" ? undefined : tagId,
        language: language === "all" ? undefined : language,
        created_after_ms: createdAfter,
      }));
    } catch (reason) {
      setOrganizationError(localizedError(reason, t));
    } finally {
      setLoading(false);
    }
  }, [api, dateRange, folderId, language, query, status, t, tagId]);

  useEffect(() => {
    const timer = window.setTimeout(() => void loadLibrary(), 250);
    return () => window.clearTimeout(timer);
  }, [loadLibrary]);

  useEffect(() => {
    let active = true;
    void Promise.all([api.listFolders(), api.listTags(), api.billingUsage()]).then(([folderRows, tagRows, usage]) => {
      if (!active) return;
      setFolders(folderRows);
      setTags(tagRows);
      const enabled = (code: string) => usage.features.some((feature) => feature.feature_code === code && Number(feature.enabled) === 1);
      const nextAvailability = { speakerDiarization: enabled("speaker_diarization"), audioEnhancement: enabled("audio_enhancement") };
      setAvailability(nextAvailability);
      setReprocessSettings((current) => applyTranscriptionFeatureAvailability(current, nextAvailability));
    }).catch((reason) => {
      if (active) setOrganizationError(localizedError(reason, t));
    });
    return () => { active = false; };
  }, [api, t]);

  const languages = [...new Set([...sharedTasks, ...tasks]
    .map((task) => task.detected_language ?? task.requested_language)
    .filter((value): value is string => Boolean(value)))].sort();

  async function deleteSelected() {
    setBulkBusy(true);
    setOrganizationError(null);
    try {
      const result = await api.bulkLibraryAction([...selected], "delete");
      const failed = result.affected.filter((item) => item.status === "failed");
      setSelected(new Set(failed.map((item) => item.transcription_id)));
      if (failed.length) setOrganizationError(t("library.bulkPartial", { failed: failed.length, total: result.affected.length }));
      else setDeletePending(false);
      await Promise.all([loadLibrary(), refreshShared()]);
    } catch (reason) {
      setOrganizationError(localizedError(reason, t));
    } finally { setBulkBusy(false); }
  }

  async function retrySelected() {
    const retryable = tasks.filter((task) => selected.has(task.id) && task.status === "failed");
    if (retryable.length === 0) return;
    setBulkBusy(true);
    setOrganizationError(null);
    try {
      const failed: typeof retryable = [];
      for (const task of retryable) {
        try { await api.retryTranscription(task.id); } catch { failed.push(task); }
      }
      setSelected(new Set(failed.map((task) => task.id)));
      if (failed.length) setOrganizationError(t("library.bulkPartial", { failed: failed.length, total: retryable.length }));
      await Promise.all([loadLibrary(), refreshShared()]);
    } catch (reason) {
      setOrganizationError(localizedError(reason, t));
    } finally { setBulkBusy(false); }
  }

  async function reprocessSelected() {
    const sources = tasks.filter((task) => selected.has(task.id));
    if (sources.length === 0) return;
    setBulkBusy(true);
    setOrganizationError(null);
    try {
      const failed: typeof sources = [];
      for (const source of sources) {
        try { await api.reprocessTranscription(source, reprocessSettings); } catch { failed.push(source); }
      }
      setSelected(new Set(failed.map((source) => source.id)));
      if (failed.length) setOrganizationError(t("library.bulkPartial", { failed: failed.length, total: sources.length }));
      setReprocessOpen(false);
      await Promise.all([loadLibrary(), refreshShared()]);
    } catch (reason) {
      setOrganizationError(localizedError(reason, t));
    } finally { setBulkBusy(false); }
  }

  async function applyBulkOrganization() {
    if (!bulkAction || !actionValue) return;
    setBulkBusy(true);
    setOrganizationError(null);
    try {
      const details = bulkAction === "move"
        ? { folder_id: actionValue === "root" ? null : actionValue }
        : { tag_id: actionValue };
      const result = await api.bulkLibraryAction([...selected], bulkAction, details);
      const failed = result.affected.filter((item) => item.status === "failed");
      setBulkAction(null);
      setActionValue("");
      setSelected(new Set(failed.map((item) => item.transcription_id)));
      if (failed.length) setOrganizationError(t("library.bulkPartial", { failed: failed.length, total: result.affected.length }));
      await Promise.all([loadLibrary(), refreshShared()]);
    } catch (reason) {
      setOrganizationError(localizedError(reason, t));
    } finally { setBulkBusy(false); }
  }

  async function createOrganization() {
    const kind = createKind;
    const name = createName.trim();
    if (!kind || !name) return;
    setBulkBusy(true);
    try {
      if (kind === "folder") {
        const folder = await api.createFolder(name);
        setFolders((current) => [...current, folder].sort((left, right) => left.name.localeCompare(right.name)));
        setBulkAction("move");
        setActionValue(folder.id);
      } else {
        const tag = await api.createTag(name);
        setTags((current) => [...current, tag].sort((left, right) => left.name.localeCompare(right.name)));
        setBulkAction("tag");
        setActionValue(tag.id);
      }
      setCreateKind(null);
      setCreateName("");
    } catch (reason) {
      setOrganizationError(localizedError(reason, t));
    } finally { setBulkBusy(false); }
  }

  return <div className="page library-page">
    <header className="page-title"><div><p className="eyebrow">{t("library.eyebrow")}</p><h1>{t("library.title")}</h1></div></header>
    <div className="library-toolbar"><label className="wide-search"><Icon name="search" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("library.search")} /></label><div className="filter-tabs">{["all", "completed", "processing", "failed"].map((item) => <button key={item} className={status === item ? "is-active" : ""} onClick={() => setStatus(item)}>{item === "completed" ? t("library.ready") : t(`library.${item}`)}</button>)}</div></div>
    <div className="library-filters">
      <select aria-label={t("library.folderFilter")} value={folderId} onChange={(event) => setFolderId(event.target.value)}><option value="all">{t("library.allFolders")}</option><option value="root">{t("library.noFolder")}</option>{folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name} ({folder.item_count})</option>)}</select>
      <select aria-label={t("library.tagFilter")} value={tagId} onChange={(event) => setTagId(event.target.value)}><option value="all">{t("library.allTags")}</option>{tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name} ({tag.item_count})</option>)}</select>
      <select aria-label={t("library.languageFilter")} value={language} onChange={(event) => setLanguage(event.target.value)}><option value="all">{t("library.allLanguages")}</option>{languages.map((value) => <option key={value} value={value}>{value}</option>)}</select>
      <select aria-label={t("library.dateFilter")} value={dateRange} onChange={(event) => setDateRange(event.target.value)}><option value="all">{t("library.anyDate")}</option><option value="7d">{t("library.last7Days")}</option><option value="30d">{t("library.last30Days")}</option></select>
      <button className="button button--ghost" onClick={() => { setCreateKind("folder"); setCreateName(""); }}><Icon name="folder" />{t("library.newFolder")}</button>
      <button className="button button--ghost" onClick={() => { setCreateKind("tag"); setCreateName(""); }}><Icon name="tag" />{t("library.newTag")}</button>
    </div>
    {organizationError && <p className="form-error" role="alert">{organizationError}</p>}
    {selected.size > 0 && <div className="bulk-wrap"><div className="bulk-bar"><strong>{t("library.bulk", { count: selected.size })}</strong><button disabled={bulkBusy} title={t("library.move")} onClick={() => { setBulkAction("move"); setActionValue(""); }}><Icon name="folder" />{t("library.move")}</button><button disabled={bulkBusy} title={t("library.tag")} onClick={() => { setBulkAction("tag"); setActionValue(""); }}><Icon name="tag" />{t("library.tag")}</button><button disabled={bulkBusy} title={t("library.reprocess")} onClick={() => setReprocessOpen(true)}><Icon name="settings" />{t("library.reprocess")}</button><button disabled={bulkBusy || !tasks.some((task) => selected.has(task.id) && task.status === "failed")} title={t("library.retryFailed")} onClick={() => void retrySelected()}><Icon name="redo" />{t("common.retry")}</button><button className="danger" disabled={bulkBusy} onClick={() => setDeletePending(true)}><Icon name="trash" />{t("library.delete")}</button></div>{bulkAction && <div className="bulk-action-panel" role="dialog" aria-label={t(`library.${bulkAction}`)}><select value={actionValue} onChange={(event) => setActionValue(event.target.value)}><option value="">{bulkAction === "move" ? t("library.chooseFolder") : t("library.chooseTag")}</option>{bulkAction === "move" && <option value="root">{t("library.noFolder")}</option>}{(bulkAction === "move" ? folders : tags).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select><button className="button button--primary" disabled={!actionValue || bulkBusy} onClick={() => void applyBulkOrganization()}>{t("common.apply")}</button><button className="button button--ghost" onClick={() => setBulkAction(null)}>{t("common.cancel")}</button></div>}</div>}
    {createKind && <div className="compact-dialog-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target && !bulkBusy) setCreateKind(null); }}><form className="panel compact-dialog" onSubmit={(event) => { event.preventDefault(); void createOrganization(); }}><header><Icon name={createKind === "folder" ? "folder" : "tag"} /><h2>{createKind === "folder" ? t("library.newFolder") : t("library.newTag")}</h2></header><label><span>{createKind === "folder" ? t("library.folderName") : t("library.tagName")}</span><input autoFocus maxLength={createKind === "folder" ? 100 : 50} value={createName} onChange={(event) => setCreateName(event.target.value)} /></label><footer><button type="button" className="button button--ghost" onClick={() => setCreateKind(null)}>{t("common.cancel")}</button><button type="submit" className="button button--primary" disabled={!createName.trim() || bulkBusy}>{bulkBusy ? t("common.loading") : t("common.create")}</button></footer></form></div>}
    {deletePending && <div className="compact-dialog-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target && !bulkBusy) setDeletePending(false); }}><section className="panel compact-dialog compact-dialog--danger" role="alertdialog" aria-modal="true"><header><Icon name="trash" /><h2>{t("library.delete")}</h2></header><p>{t("library.deleteConfirm", { count: selected.size })}</p><footer><button className="button button--ghost" onClick={() => setDeletePending(false)}>{t("common.cancel")}</button><button className="button danger-button" disabled={bulkBusy} onClick={() => void deleteSelected()}>{bulkBusy ? t("common.loading") : t("library.delete")}</button></footer></section></div>}
    {reprocessOpen && <div className="reprocess-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target && !bulkBusy) setReprocessOpen(false); }}><div className="reprocess-dialog"><header><strong>{t("library.reprocessSummary", { count: selected.size })}</strong><span>{t("library.reprocessEstimate", { duration: estimatedDuration(tasks.filter((task) => selected.has(task.id)).map((task) => task.duration_ms), i18n.language) })}</span></header><div className="reprocess-items">{tasks.filter((task) => selected.has(task.id)).map((task) => <div key={task.id}><span>{task.title}</span><strong>{estimatedDuration([task.duration_ms], i18n.language)}</strong></div>)}</div><TranscriptionSettings open settings={reprocessSettings} onChange={setReprocessSettings} onClose={() => setReprocessOpen(false)} onStart={() => void reprocessSelected()} busy={bulkBusy} quotaSummary={t("library.reprocessEstimate", { duration: estimatedDuration(tasks.filter((task) => selected.has(task.id)).map((task) => task.duration_ms), i18n.language) })} startLabel={t("library.confirmReprocess")} availability={availability} /></div></div>}
    {loading ? <div className="panel empty-state">{t("common.loading")}</div> : <SelectableTaskTable tasks={tasks} selected={selected} setSelected={setSelected} />}
  </div>;
}

function estimatedDuration(values: Array<number | null>, locale: string): string {
  const totalSeconds = Math.ceil(values.reduce<number>((total, value) => total + (value ?? 0), 0) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.ceil(totalSeconds % 3600 / 60);
  const hoursLabel = new Intl.NumberFormat(locale, { style: "unit", unit: "hour", unitDisplay: "short" }).format(hours);
  const minutesLabel = new Intl.NumberFormat(locale, { style: "unit", unit: "minute", unitDisplay: "short" }).format(minutes);
  return hours ? `${hoursLabel} ${minutesLabel}` : minutesLabel;
}

function SelectableTaskTable({ tasks, selected, setSelected }: { tasks: ReturnType<typeof useTasks>["tasks"]; selected: Set<string>; setSelected: (value: Set<string>) => void }) {
  const { t } = useTranslation();
  if (!tasks.length) return <TaskTable tasks={[]} />;
  return <div className="library-select-list"><label className="select-all"><input aria-label={t("library.selectAll")} type="checkbox" checked={selected.size === tasks.length} onChange={(event) => setSelected(event.target.checked ? new Set(tasks.map((task) => task.id)) : new Set())} /> <span>{selected.size}/{tasks.length}</span></label><TaskTable tasks={tasks} />{tasks.map((task, index) => <input key={task.id} aria-label={t("library.selectTask", { title: task.title })} className="task-checkbox" style={{ top: 58 + index * 72 }} type="checkbox" checked={selected.has(task.id)} onChange={(event) => { const next = new Set(selected); if (event.target.checked) next.add(task.id); else next.delete(task.id); setSelected(next); }} />)}</div>;
}
